import { Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import {
  CLIENT_STREAM_PATH,
  VOICE_CALL_LABELS,
  WS_RECONNECT_BASE_MS,
  WS_RECONNECT_MAX_MS,
} from '../constants/voice-call.constants';
import {
  CallUiState,
  ClientStreamMessage,
  TranscriptLine,
} from '../models/voice-call.models';

@Injectable({ providedIn: 'root' })
export class ClientStreamService implements OnDestroy {
  readonly streamState = signal<CallUiState>('idle');
  readonly statusMessage = signal<string | null>(null);
  readonly transcriptLines = signal<TranscriptLine[]>([]);
  readonly wsConnected = signal(false);

  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private shouldReconnect = true;
  private lineCounter = 0;

  connect(): void {
    this.shouldReconnect = true;
    this.openSocket();
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  disconnect(): void {
    this.teardown();
  }

  clearTranscript(): void {
    this.transcriptLines.set([]);
  }

  private openSocket(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    const httpBase = environment.pythonUrl.replace(/\/$/, '');
    const wsBase = httpBase.replace(/^http/, 'ws');
    const key = encodeURIComponent(environment.apiKey);
    const url = `${wsBase}${CLIENT_STREAM_PATH}?x-api-key=${key}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.wsConnected.set(true);
      this.reconnectAttempt = 0;
    };

    this.ws.onmessage = (event) => this.handleMessage(event.data);

    this.ws.onclose = () => {
      this.wsConnected.set(false);
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.wsConnected.set(false);
    };
  }

  private handleMessage(raw: unknown): void {
    let msg: ClientStreamMessage;
    try {
      msg = JSON.parse(String(raw)) as ClientStreamMessage;
    } catch {
      return;
    }

    if (msg?.type === 'status') {
      this.streamState.set(msg.state);
      this.statusMessage.set(msg.message ?? null);
      return;
    }

    if (msg?.type === 'transcript' && msg.text?.trim()) {
      this.lineCounter += 1;
      const line: TranscriptLine = {
        id: `t-${this.lineCounter}`,
        role: msg.role,
        text: msg.text.trim(),
      };
      this.transcriptLines.update((lines) => [...lines, line]);
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect) {
      return;
    }

    this.statusMessage.set(VOICE_CALL_LABELS.wsDisconnected);
    const delay = Math.min(
      WS_RECONNECT_BASE_MS * 2 ** this.reconnectAttempt,
      WS_RECONNECT_MAX_MS,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private teardown(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.wsConnected.set(false);
  }
}
